import React, { useState, useEffect } from 'react';
import MainLayout from '../layout/MainLayout';
import planApi from '../../services/planApi';
import config from '../../config';
import { ChevronDown } from 'lucide-react';
import './admin-milestones.scss';

interface Dupla {
  relationship_id: number;
  mentee: string;
  mentor: string;
  mentee_image: string | null;
  mentor_image: string | null;
  foda: boolean;
  pdi: boolean;
}

interface MilestonesData {
  total: number;
  foda_done: number;
  pdi_done: number;
  duplas: Dupla[];
}

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
  return `${baseUrl}/${imagePath}`;
};

const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const Avatars: React.FC<{ dupla: Dupla }> = ({ dupla }) => {
  const menteeImg = getImageUrl(dupla.mentee_image);
  const mentorImg = getImageUrl(dupla.mentor_image);
  return (
    <div className="hito-avatars">
      <span className="hito-avatar hito-avatar--back">
        {mentorImg ? <img src={mentorImg} alt={dupla.mentor} /> : <span className="hito-avatar__ini">{initials(dupla.mentor)}</span>}
      </span>
      <span className="hito-avatar hito-avatar--front">
        {menteeImg ? <img src={menteeImg} alt={dupla.mentee} /> : <span className="hito-avatar__ini">{initials(dupla.mentee)}</span>}
      </span>
    </div>
  );
};

const eyebrowDate = () => {
  const d = new Date();
  const dia = d.toLocaleDateString('es', { weekday: 'long' });
  const num = d.getDate();
  const mes = d.toLocaleDateString('es', { month: 'short' }).replace('.', '');
  return `${dia} ${num} de ${mes} · Administrador`.toUpperCase();
};

interface MilestoneCardProps {
  title: string;
  done: number;
  total: number;
  duplas: Dupla[];
  field: 'foda' | 'pdi';
  defaultOpen?: boolean;
}

const MilestoneCard: React.FC<MilestoneCardProps> = ({ title, done, total, duplas, field, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className={`hito-card ${open ? 'is-open' : ''}`}>
      <button className="hito-card__head" onClick={() => setOpen(o => !o)}>
        <h2 className="hito-card__title">{title}</h2>
        <div className="hito-card__meta">
          <span className="hito-card__count"><em>{done}</em>/{total}</span>
          <span className="hito-card__bar"><span className="hito-card__fill" style={{ width: `${pct}%` }} /></span>
          <ChevronDown size={20} className="hito-card__chev" />
        </div>
      </button>
      {open && (
        <div className="hito-card__body">
          {duplas.length === 0 ? (
            <p className="hito-empty">No hay duplas registradas.</p>
          ) : duplas.map(d => (
            <div className="hito-row" key={d.relationship_id}>
              <Avatars dupla={d} />
              <div className="hito-row__names">
                <span className="hito-row__mentee">{d.mentee}</span>
                <span className="hito-row__mentor">↪ {d.mentor}</span>
              </div>
              <span className={`hito-pill ${d[field] ? 'is-done' : 'is-pending'}`}>
                {d[field] ? 'Terminado' : 'Sin registro'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminMilestones: React.FC = () => {
  const [data, setData] = useState<MilestonesData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    planApi.getMilestones()
      .then((res: MilestonesData) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <MainLayout>
      <div className="admin-milestones">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{eyebrowDate()}</div>
            <h1 className="heading-1">Hitos de las <em>duplas</em></h1>
            <p className="hito-intro">
              Creación del FODA y del PDI. Despliega cada hito para ver el estado de cada dupla.
            </p>
            <p className="hito-intro">
              Los dos hitos que cada dupla completa al inicio. El <strong>FODA</strong> lo termina el mentee;
              el <strong>PDI</strong> lo construye la dupla. Despliega cada uno para ver quién ya lo completó.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="hito-loading">Cargando hitos…</div>
        ) : !data ? (
          <div className="hito-loading">No se pudieron cargar los hitos.</div>
        ) : (
          <div className="hito-cards">
            <MilestoneCard title="Creación de FODAs" done={data.foda_done} total={data.total} duplas={data.duplas} field="foda" defaultOpen />
            <MilestoneCard title="Creación de PDI" done={data.pdi_done} total={data.total} duplas={data.duplas} field="pdi" />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminMilestones;
